import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, RectangleHorizontal, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Banner = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/ads/banners`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || '',
        image_url: item.image_url || '',
        link: item.link || '',
        is_active: item.is_active !== false,
      });
    } else {
      setEditingItem(null);
      setFormData({ title: '', image_url: '', link: '', is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/admin/ads/banners/${editingItem.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/ads/banners`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/ads/banners/${id}`, { withCredentials: true });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleToggle = async (item) => {
    try {
      await axios.patch(`${API_URL}/api/admin/ads/banners/${item.id}/toggle`, {}, { withCredentials: true });
      setItems(prev => prev.map(b => b.id === item.id ? { ...b, is_active: !b.is_active } : b));
    } catch (error) {
      console.error('Failed to toggle:', error);
    }
  };

  return (
    <div data-testid="banner-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Banner Ads</h1>
          <p className="font-body text-text-secondary">Manage promotional banner advertisements</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
          data-testid="add-banner-btn"
        >
          <Plus size={18} />
          Add Banner
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-text-muted">
            <RectangleHorizontal size={48} className="mb-4 opacity-40" />
            <p className="font-body">No banners yet</p>
            <Button variant="link" onClick={() => handleOpenDialog()} className="text-brand-primary">
              Add your first banner
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-background-secondary">
                <TableHead className="font-heading font-semibold">Image</TableHead>
                <TableHead className="font-heading font-semibold">Title</TableHead>
                <TableHead className="font-heading font-semibold">Link</TableHead>
                <TableHead className="font-heading font-semibold">Status</TableHead>
                <TableHead className="font-heading font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="w-24 h-14 rounded-lg object-cover border border-surface-border" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-24 h-14 rounded-lg bg-background-secondary flex items-center justify-center">
                        <RectangleHorizontal size={20} className="text-text-muted" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-body font-medium">{item.title || '-'}</TableCell>
                  <TableCell>
                    {item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-brand-primary hover:underline text-sm font-body truncate max-w-[200px]">
                        <ExternalLink size={14} />
                        {item.link}
                      </a>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={item.is_active !== false} onCheckedChange={() => handleToggle(item)} data-testid={`banner-toggle-${item.id}`} />
                      <span className={`text-xs font-body ${item.is_active !== false ? 'text-green-600' : 'text-text-muted'}`}>
                        {item.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)} className="text-text-secondary hover:text-brand-primary" title="Edit">
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-text-secondary hover:text-red-600" title="Delete">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingItem ? 'Edit' : 'Add'} Banner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="banner-title" className="font-body font-medium">Title</Label>
              <Input
                id="banner-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Banner title"
                className="rounded-xl"
                data-testid="banner-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="banner-image" className="font-body font-medium">Image URL</Label>
              <Input
                id="banner-image"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/banner.jpg"
                className="rounded-xl"
                data-testid="banner-image-input"
              />
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="w-full h-32 rounded-xl object-cover border border-surface-border mt-2" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="banner-link" className="font-body font-medium">Link</Label>
              <Input
                id="banner-link"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://example.com"
                className="rounded-xl"
                data-testid="banner-link-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !formData.title} className="bg-brand-primary hover:bg-brand-primary-hover rounded-xl" data-testid="banner-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Banner;
